"use client";

import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import assetLoader from "@utils/internalImageLoder";
import { useShortlist } from "@hooks/useShortlist";
import { useWishlistToast } from "@hooks/useWishlistToast";
import WishlistToast from "@components/atoms/WishlistToast";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { getPageNameFromUrl } from "@utils/pageNameUtils";
import { getLoginUrl } from "@utils/getLoginUrl";
import { handleLoginFlow } from "@utils/loginFlowHandler";
import { LoginSdkConfig } from "@enums/LoginSdkConfig";
import styles from "./AppMMShortlistButton.module.scss";

interface AppMMShortlistButtonProps {
  slug?: string;
  brandName?: string;
  modelName?: string;
  cityName: string;
  isLoggedIn: boolean;
  status?: string;
}

const AppMMShortlistButton = ({
  slug,
  brandName,
  modelName,
  cityName,
  isLoggedIn,
  status,
}: AppMMShortlistButtonProps) => {
  const pathname = usePathname();
  const router = useRouter();
  const { isCarShortlisted, toggleShortlistItem } = useShortlist();
  const { toast, showToast, hideToast } = useWishlistToast();
  const isShortlisted = slug ? isCarShortlisted(slug) : false;

  const handleClick = async () => {
    if (!slug || !brandName || !modelName) return;

    await trackEventWithCommonProps("action", EventType.TRACK_EVENT, {
      pageType: getPageNameFromUrl(pathname),
      additionalProps: {
        action_type: "tap_btn",
        action_name: "tap_btn_wishlist_car",
        car_availability: status === "sellable" ? "yes" : "no",
        vehicle_make: brandName,
        vehicle_model: modelName,
        city_name: cityName,
      },
    });

    if (!isLoggedIn) {
      if (typeof window !== "undefined") {
        sessionStorage.setItem("loginType", "Wishlist");
        localStorage.setItem(
          "MMV",
          JSON.stringify({
            make: brandName,
            model: modelName,
          })
        );
        await handleLoginFlow(
          getLoginUrl(window.location.href),
          LoginSdkConfig.WISHLIST
        );
      }
      return;
    }

    const car = { id: slug, make: brandName, name: modelName };
    const result = await toggleShortlistItem(car);
    if (result.success) {
      showToast(car, !isShortlisted);
    }
  };

  if (!slug || !brandName || !modelName) return null;

  return (
    <>
      <button
        type="button"
        className={styles.button}
        onClick={handleClick}
        aria-label={isShortlisted ? "Remove from shortlist" : "Add to shortlist"}
      >
        <Image
          src={
            isShortlisted
              ? `${assetLoader()}/icons/shortlistLiked.webp`
              : `${assetLoader()}/icons/shortlistUnLikedDark.webp`
          }
          alt=""
          width={20}
          height={20}
          aria-hidden
        />
      </button>
      <WishlistToast
        isVisible={toast.show}
        isAdding={toast.isAdding}
        carMake={toast.carMake}
        carName={toast.carName}
        hasBottomNav={false}
        isInApp
        onViewWishlist={() => router.push("/wishlist")}
        onUndo={async () => {
          const car = { id: slug, make: brandName, name: modelName };
          await toggleShortlistItem(car);
          hideToast();
        }}
      />
    </>
  );
};

export default AppMMShortlistButton;
